// ============================================================
// ANSICHTSSACHE-AGENT – Farben fuer den Kuerzel-Kreis
// ============================================================
// Eine Person kann ihrem Kreis eine Farbe geben (Stammdaten-
// Dialog, Klick auf den Kreis): aus der Palette oder frei im
// Farbwaehler. Gespeichert wird ein Farbwert (`#7c3aed`).
//
// Bis zum Nachmittag des 24.09.2026 gab es nur die Palette, und
// gespeichert wurde der NAME („violet“). Solche Namen koennen noch
// in der Datenbank stehen; `personColor` liest sie weiter und
// macht den Farbwert der Palette daraus.
//
// WIE DER KREIS AUS EINEM BELIEBIGEN WERT LESBAR WIRD: nicht mit
// festen Klassen je Farbe (die kennt Tailwind nur, wenn sie
// woertlich im Quelltext stehen), sondern mit EINER Variablen
// (`--person-color`, gesetzt per `style`) und Klassen, die daraus
// mischen — heller Grund, dunkle Schrift; im dunklen Modus
// umgekehrt. Wer einen Kreis zeichnet, braucht deshalb beides:
// `avatarColorClasses` fuer die Klasse und `avatarColorStyle` fuer
// den Wert.
// ============================================================

import type { CSSProperties } from "react";

export type PersonColorPreset = {
  /** Der frueher gespeicherte Name — nur noch zum Lesen alter Werte. */
  key: string;
  label: string;
  hex: string;
};

/** Die Palette, in der Reihenfolge des Farbkreises. Werte: Tailwind, Stufe 500. */
export const PERSON_COLOR_PRESETS: readonly PersonColorPreset[] = [
  { key: "red", label: "Rot", hex: "#ef4444" },
  { key: "orange", label: "Orange", hex: "#f97316" },
  { key: "amber", label: "Bernstein", hex: "#f59e0b" },
  { key: "yellow", label: "Gelb", hex: "#eab308" },
  { key: "lime", label: "Limette", hex: "#84cc16" },
  { key: "green", label: "Grün", hex: "#22c55e" },
  { key: "emerald", label: "Smaragd", hex: "#10b981" },
  { key: "teal", label: "Blaugrün", hex: "#14b8a6" },
  { key: "cyan", label: "Türkis", hex: "#06b6d4" },
  { key: "sky", label: "Himmel", hex: "#0ea5e9" },
  { key: "blue", label: "Blau", hex: "#3b82f6" },
  { key: "indigo", label: "Indigo", hex: "#6366f1" },
  { key: "violet", label: "Violett", hex: "#8b5cf6" },
  { key: "purple", label: "Lila", hex: "#a855f7" },
  { key: "fuchsia", label: "Fuchsie", hex: "#d946ef" },
  { key: "pink", label: "Pink", hex: "#ec4899" },
  { key: "rose", label: "Rose", hex: "#f43f5e" },
  { key: "stone", label: "Stein", hex: "#78716c" },
];

/**
 * Ohne gewaehlte Farbe: der Gegenakzent. Ein Gesicht BEZEICHNET
 * jemanden, es hebt ihn nicht hervor — im Hausrot bliebe fuer
 * „ueberfaellig“ keine Farbe uebrig (Begruendung in `tokens.css`
 * unter „Gegenakzent“).
 */
export const DEFAULT_AVATAR_CLASSES = "bg-accent-2/12 text-accent-2";

/**
 * Grund und Schrift aus `--person-color`. Die Schrift ist die Farbe
 * mit Schwarz gemischt, damit auch Gelb auf seinem hellen Grund
 * lesbar bleibt; im dunklen Modus mit Weiss. `color:` sagt Tailwind,
 * dass `text-[…]` eine Farbe meint und keine Schriftgroesse.
 */
const CUSTOM_AVATAR_CLASSES = [
  "bg-[color:color-mix(in_oklab,var(--person-color)_16%,transparent)]",
  "text-[color:color-mix(in_oklab,var(--person-color)_62%,black)]",
  "dark:bg-[color:color-mix(in_oklab,var(--person-color)_30%,transparent)]",
  "dark:text-[color:color-mix(in_oklab,var(--person-color)_55%,white)]",
].join(" ");

const LEGACY = new Map(PERSON_COLOR_PRESETS.map((preset) => [preset.key, preset.hex]));
const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Macht aus einer Eingabe einen Farbwert `#rrggbb` (klein) — aus
 * `#abc`, `abc`, `#AABBCC` oder einem alten Palettennamen.
 * `null`, wenn es keiner ist.
 */
export function normalizePersonColor(value: string): string | null {
  const text = value.trim().toLowerCase();
  const legacy = LEGACY.get(text);
  if (legacy) return legacy;

  const match = HEX.exec(text);
  if (!match) return null;
  const digits = match[1];
  const full =
    digits.length === 3
      ? digits
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : digits;
  return `#${full}`;
}

/** Der Farbwert zum Zeichnen; `null` = Vorgabe (auch bei einem kaputten Wert). */
export function personColor(value: string | null | undefined): string | null {
  return value ? normalizePersonColor(value) : null;
}

/** Die Klassen des Kreises — immer zusammen mit `avatarColorStyle`. */
export function avatarColorClasses(value: string | null | undefined): string {
  return personColor(value) ? CUSTOM_AVATAR_CLASSES : DEFAULT_AVATAR_CLASSES;
}

/** Der Farbwert als Variable fuer die Klassen von `avatarColorClasses`. */
export function avatarColorStyle(value: string | null | undefined): CSSProperties | undefined {
  const hex = personColor(value);
  return hex ? ({ "--person-color": hex } as CSSProperties) : undefined;
}

/** Der Name der Farbe, wenn sie aus der Palette stammt. */
export function presetLabel(value: string | null | undefined): string | null {
  const hex = personColor(value);
  return PERSON_COLOR_PRESETS.find((preset) => preset.hex === hex)?.label ?? null;
}

// ── Umrechnung fuer den Farbwaehler ─────────────────────────
// Der Waehler arbeitet in HSV (Farbton, Saettigung, Hellwert):
// die Flaeche ist Saettigung × Hellwert, der Regler der Farbton.

export type Hsv = { h: number; s: number; v: number };

/** `#rrggbb` → HSV mit h in Grad (0–360), s und v von 0 bis 1. */
export function hexToHsv(hex: string): Hsv {
  const value = normalizePersonColor(hex) ?? "#000000";
  const r = parseInt(value.slice(1, 3), 16) / 255;
  const g = parseInt(value.slice(3, 5), 16) / 255;
  const b = parseInt(value.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta > 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: max === 0 ? 0 : delta / max, v: max };
}

/** HSV → `#rrggbb`. */
export function hsvToHex({ h, s, v }: Hsv): string {
  const hue = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = v - c;

  const [r, g, b] =
    hue < 60
      ? [c, x, 0]
      : hue < 120
        ? [x, c, 0]
        : hue < 180
          ? [0, c, x]
          : hue < 240
            ? [0, x, c]
            : hue < 300
              ? [x, 0, c]
              : [c, 0, x];

  const channel = (value: number) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}
